import { createAsyncThunk, ThunkDispatch } from '@reduxjs/toolkit';
import candidateService from './Candidate.service';
import { PersonListParams } from './Candidate.type';
import {
  PersonCreateFetch,
  PersonMergeFetch,
  PersonMergeWithDataFetch,
  PersonUpdateFetch,
} from './Experience/PersonExperience.type';
import { setGlobalLoading } from '../App/App.slice';
import { syncPersonLinkThunk } from './Link/PersonLink.thunk';
import { syncAttachmentsThunk } from '../Attachment/Attachment.thunk';
import { PERSON_ATTACH_OBJECT } from '../../constants/attachment.constant';
import { errorHandlerThunk } from '../App/App.thunk';
import { syncTagsThunk } from '../Tag/Tag.thunk';
import { formatAttachmentsFiles } from '../../utils/formatAttachmentsFiles';

export const getCandidateMetaThunk = createAsyncThunk(
  'candidate/getCandidateMetaThunk',
  async (region: number[] | undefined, thunkAPI) => {
    try {
      const response = await candidateService.getMeta(region);
      return response.data.meta;
    } catch (error) {
      throw thunkAPI.dispatch(errorHandlerThunk(error));
    }
  },
);

export const getCandidateListThunk = createAsyncThunk(
  'candidate/getCandidateListThunk',
  async (params: PersonListParams, thunkApi) => {
    try {
      const response = await candidateService.getList(params);
      return response.data;
    } catch (error) {
      throw thunkApi.dispatch(errorHandlerThunk(error));
    }
  },
);

export const getCandidateSearchThunk = createAsyncThunk(
  'candidate/getCandidateSearchThunk',
  async (params: PersonListParams, thunkApi) => {
    try {
      const response = await candidateService.getList(params);
      return response.data;
    } catch (error) {
      throw thunkApi.dispatch(errorHandlerThunk(error));
    }
  },
);

export const getCandidateInfoThunk = createAsyncThunk(
  'candidate/getCandidateInfoThunk',
  async (id: number | string, thunkApi) => {
    try {
      const response = await candidateService.getInfo(id);
      return response.data.data;
    } catch (error) {
      thunkApi.dispatch(errorHandlerThunk(error));
    }
  },
);

export const getCandidatePreviewThunk = createAsyncThunk(
  'candidate/getCandidatePreviewThunk',
  async (id: number | string, thunkApi) => {
    const response = await candidateService.getPreviewInfo(id, {
      signal: thunkApi.signal,
    });
    return response.data.data;
  },
);

export const createPersonThunk =
  (params: PersonCreateFetch) => (dispatch: ThunkDispatch<any, any, any>) => {
    return new Promise(resolve =>
      candidateService
        .store(params)
        .then(async ({ data: info }: any) => {
          const { data } = info;

          if (params.links) {
            await dispatch(syncPersonLinkThunk(data.id, params.links));
          }

          if (params.attachments) {
            await dispatch(
              syncAttachmentsThunk(
                {
                  type: PERSON_ATTACH_OBJECT,
                  id: data.id,
                },
                formatAttachmentsFiles(params.attachments),
              ),
            );
          }
          if (params.tags && params.tags?.length > 0) {
            await dispatch(
              syncTagsThunk({
                objectType: PERSON_ATTACH_OBJECT,
                tags: params.tags.map(tag => tag.id),
                objectId: data.id,
              }),
            );
          }
          resolve(data);
          dispatch(getCandidateInfoThunk(data.id));
          dispatch(setGlobalLoading(false));
        })
        .catch(err => {
          dispatch(errorHandlerThunk(err));
          resolve(false);
        }),
    );
  };

export const checkPersonThunk =
  (params: PersonCreateFetch) => (dispatch: ThunkDispatch<any, any, any>) => {
    return candidateService
      .check(params)
      .then(res => {
        return [true, res];
      })
      .catch(err => {
        dispatch(errorHandlerThunk(err));
        return [false, err];
      });
  };

export const mergePersonThunk =
  (targetId: number | string, params: PersonMergeFetch) =>
  async (dispatch: ThunkDispatch<any, any, any>) => {
    if (params.attachments) {
      await dispatch(
        syncAttachmentsThunk(
          {
            type: PERSON_ATTACH_OBJECT,
            id: targetId,
          },
          formatAttachmentsFiles(params.attachments),
        ),
      );
    }
    return candidateService
      .merge(targetId, params)
      .then(({ data }: any) => {
        return data.id;
      })
      .catch(err => {
        dispatch(errorHandlerThunk(err));
        return false;
      });
  };

export const updatePersonThunk =
  (params: PersonUpdateFetch) => (dispatch: ThunkDispatch<any, any, any>) => {
    return new Promise(resolve =>
      candidateService
        .update(params)
        .then(async () => {
          if (params.links) {
            await dispatch(syncPersonLinkThunk(params.id, params.links));
          }

          if (params.attachments) {
            await dispatch(
              syncAttachmentsThunk(
                {
                  type: PERSON_ATTACH_OBJECT,
                  id: params.id,
                },
                formatAttachmentsFiles(params.attachments),
              ),
            );
          }

          await dispatch(
            syncTagsThunk({
              objectType: PERSON_ATTACH_OBJECT,
              tags: (params.tags || []).map(tag => tag.id),
              objectId: params.id,
            }),
          );
          resolve(params);
          dispatch(getCandidateInfoThunk(params.id));
        })
        .catch(err => {
          dispatch(errorHandlerThunk(err));
          resolve(false);
        }),
    );
  };

export const destroyPersonThunk =
  (id: number | string) => (dispatch: ThunkDispatch<any, any, any>) => {
    return candidateService
      .destroy(id)
      .then(() => {
        // dispatch(getCandidateInfoThunk(id));  // it leads to reload the application
        return true;
      })
      .catch(err => {
        dispatch(errorHandlerThunk(err));

        return false;
      });
  };

export const getCandidateMergeWithDataThunk = createAsyncThunk(
  'candidate/getCandidateMergeWithDataThunk',
  async (params: PersonMergeWithDataFetch, { dispatch }): Promise<void> => {
    try {
      if (params.attachments) {
        await dispatch(
          syncAttachmentsThunk(
            {
              type: PERSON_ATTACH_OBJECT,
              id: params.id,
            },
            formatAttachmentsFiles(params.attachments),
          ),
        );
      }

      if (params.links) {
        await dispatch(syncPersonLinkThunk(params?.id, params.links));
      }
      await candidateService.mergeWithData(params);
    } catch (error) {
      dispatch(errorHandlerThunk(error));
    }
  },
);
